/**
 * SkillsUSA Upload Modal
 * Supports single-file and 3-file import modes
 */

import React, { useState, useRef } from 'react';

type ImportMode = 'single' | 'three-file';

interface SkillsUSAUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadThreeFile: (banksFile: File, questionsFile: File, optionsFile: File, createAssessment: boolean, assessmentName: string) => void;
  onUploadSingleFile: (csvFile: File, createAssessment: boolean, assessmentName: string) => void;
  isUploading?: boolean;
}

export const SkillsUSAUploadModal: React.FC<SkillsUSAUploadModalProps> = ({
  isOpen,
  onClose,
  onUploadThreeFile,
  onUploadSingleFile,
  isUploading = false,
}) => {
  const [mode, setMode] = useState<ImportMode>('single');
  const [singleFile, setSingleFile] = useState<File | null>(null);
  const [banksFile, setBanksFile] = useState<File | null>(null);
  const [questionsFile, setQuestionsFile] = useState<File | null>(null);
  const [optionsFile, setOptionsFile] = useState<File | null>(null);
  const [createAssessment, setCreateAssessment] = useState(false);
  const [assessmentName, setAssessmentName] = useState('');
  const singleRef = useRef<HTMLInputElement>(null);
  const banksRef = useRef<HTMLInputElement>(null);
  const questionsRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const canUpload = mode === 'single'
    ? !!singleFile
    : !!(banksFile && questionsFile && optionsFile);

  const validateFile = (file: File): string | null => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return `"${file.name}" is not a CSV file`;
    }
    return null;
  };

  const handleUpload = () => {
    if (mode === 'single') {
      if (!singleFile) return;
      const err = validateFile(singleFile);
      if (err) { alert(err); return; }
      onUploadSingleFile(singleFile, createAssessment, assessmentName.trim());
    } else {
      if (!banksFile || !questionsFile || !optionsFile) return;
      const errors = [
        validateFile(banksFile),
        validateFile(questionsFile),
        validateFile(optionsFile),
      ].filter(Boolean);
      if (errors.length > 0) { alert(errors.join('\n')); return; }
      onUploadThreeFile(banksFile, questionsFile, optionsFile, createAssessment, assessmentName.trim());
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    clearFiles();
    onClose();
  };

  const clearFiles = () => {
    setSingleFile(null);
    setBanksFile(null);
    setQuestionsFile(null);
    setOptionsFile(null);
    setCreateAssessment(false);
    setAssessmentName('');
  };

  const switchMode = (newMode: ImportMode) => {
    if (newMode === mode) return;
    clearFiles();
    setMode(newMode);
  };

  const fileInputStyle: React.CSSProperties = { marginBottom: '1rem' };
  const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 'bold', marginBottom: '0.25rem' };
  const descStyle: React.CSSProperties = { fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '0.5rem 1rem',
    border: '1px solid #ccc',
    background: active ? '#4a90d9' : '#fff',
    color: active ? '#fff' : '#333',
    cursor: 'pointer',
    fontWeight: active ? 'bold' : 'normal',
    fontSize: '0.9rem',
  });

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '550px' }}
      >
        <div className="modal-header">
          <h2>Import SkillsUSA Assessment</h2>
          <button
            className="modal-close"
            onClick={handleClose}
            disabled={isUploading}
          >
            x
          </button>
        </div>

        <div className="modal-body">
          <div style={{ display: 'flex', marginBottom: '1.5rem', borderRadius: '4px', overflow: 'hidden' }}>
            <button
              style={{ ...tabStyle(mode === 'single'), borderRadius: '4px 0 0 4px' }}
              onClick={() => switchMode('single')}
              disabled={isUploading}
            >
              Single File
            </button>
            <button
              style={{ ...tabStyle(mode === 'three-file'), borderRadius: '0 4px 4px 0', borderLeft: 'none' }}
              onClick={() => switchMode('three-file')}
              disabled={isUploading}
            >
              Three Files
            </button>
          </div>

          {mode === 'single' ? (
            <>
              <p style={{ marginBottom: '1.5rem', color: '#555' }}>
                Upload a single CSV file containing question banks, questions, and answers.
              </p>
              <div style={fileInputStyle}>
                <label style={labelStyle} htmlFor="skillsusa-single-file">Assessment CSV</label>
                <div style={descStyle}>
                  Combined file with columns: Question Bank Id, Question Bank Name, Question Name, Question Order, Question Type, Question Answer, Question Answer Order, Question Answer Is Correct
                </div>
                <input
                  id="skillsusa-single-file"
                  ref={singleRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setSingleFile(e.target.files?.[0] || null)}
                  disabled={isUploading}
                />
              </div>
            </>
          ) : (
            <>
              <p style={{ marginBottom: '1.5rem', color: '#555' }}>
                Upload three CSV files to import a SkillsUSA assessment. This will create
                multiple question banks with their questions and answers.
              </p>
              <div style={fileInputStyle}>
                <label style={labelStyle} htmlFor="skillsusa-banks-file">Question Banks CSV</label>
                <div style={descStyle}>
                  Contains bank definitions (Name, ExternalId, Assessment Name)
                </div>
                <input
                  id="skillsusa-banks-file"
                  ref={banksRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setBanksFile(e.target.files?.[0] || null)}
                  disabled={isUploading}
                />
              </div>
              <div style={fileInputStyle}>
                <label style={labelStyle} htmlFor="skillsusa-questions-file">Questions CSV</label>
                <div style={descStyle}>
                  Contains questions linked to banks (Name, Question_ExternalId, QuestionBank_ExternalId, ...)
                </div>
                <input
                  id="skillsusa-questions-file"
                  ref={questionsRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setQuestionsFile(e.target.files?.[0] || null)}
                  disabled={isUploading}
                />
              </div>
              <div style={fileInputStyle}>
                <label style={labelStyle} htmlFor="skillsusa-options-file">Answers CSV</label>
                <div style={descStyle}>
                  Contains answer options (Question_ExternalId, Name, IsCorrect, Order)
                </div>
                <input
                  id="skillsusa-options-file"
                  ref={optionsRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => setOptionsFile(e.target.files?.[0] || null)}
                  disabled={isUploading}
                />
              </div>
            </>
          )}

          <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={createAssessment}
                onChange={(e) => setCreateAssessment(e.target.checked)}
                disabled={isUploading}
              />
              <span style={{ fontWeight: 'bold' }}>Make an assessment from this import</span>
            </label>
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
              Creates a draft assessment with all imported question banks linked to it
            </div>
            {createAssessment && (
              <div style={{ marginTop: '0.75rem', marginLeft: '1.5rem' }}>
                <label htmlFor="skillsusa-assessment-name" style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                  Assessment Name
                </label>
                <input
                  id="skillsusa-assessment-name"
                  type="text"
                  value={assessmentName}
                  onChange={(e) => setAssessmentName(e.target.value)}
                  placeholder="e.g. SkillsUSA Carpentry 2024"
                  disabled={isUploading}
                  style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
                  This name will also prefix each question bank name for better organization
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="modal-footer"
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}
        >
          <button onClick={handleClose} className="btn btn-secondary" disabled={isUploading}>
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="btn btn-primary"
            disabled={!canUpload || isUploading}
          >
            {isUploading ? 'Analyzing...' : 'Analyze Files'}
          </button>
        </div>
      </div>
    </div>
  );
};
